/**
 * AquiferSummaryTable - Hierarchical table for Aquifer → Year → Agg System
 */

import React from "react";
import { Table, TableBody, Paper, Typography } from "@material-ui/core";
import {
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from "@material-ui/icons";
import { useExpandable } from "../../../hooks";
import { DataCells } from "../../molecules";
import {
  SummaryTableContainer,
  StickyFirstCell,
  IndentedCell,
  GroupRow,
  ChildRow,
  GrandchildRow,
  TotalsRow,
  ExpandIcon,
  NoDataMessage,
} from "../../styled";
import SummaryHeader from "./SummaryHeader";

/**
 * Aquifer summary table component
 * @param {Object} props
 * @param {import("../../../types").AquiferSummary} props.summary - Aquifer summary data
 * @param {string} props.columnMode - Column display mode
 */
const AquiferSummaryTable = ({ summary, columnMode }) => {
  const { expandedL1, expandedL2, toggleExpandL1, toggleExpandL2 } =
    useExpandable();

  if (!summary || summary.aquifers.length === 0) {
    return (
      <NoDataMessage>
        <Typography variant="body1">No data for selected filters.</Typography>
      </NoDataMessage>
    );
  }

  return (
    <SummaryTableContainer component={Paper} variant="outlined">
      <Table size="small" stickyHeader>
        <SummaryHeader firstColumnLabel="Aquifer" columnMode={columnMode} />
        <TableBody>
          {summary.aquifers.map((aquifer) => (
            <React.Fragment key={aquifer.aquifer_ndx}>
              <GroupRow onClick={() => toggleExpandL1(aquifer.aquifer_ndx)}>
                <StickyFirstCell>
                  <ExpandIcon>
                    {expandedL1[aquifer.aquifer_ndx] ? (
                      <ExpandMoreIcon fontSize="small" />
                    ) : (
                      <ChevronRightIcon fontSize="small" />
                    )}
                  </ExpandIcon>
                  {aquifer.aquifer_name}
                </StickyFirstCell>
                <DataCells
                  totals={aquifer.totals}
                  pctPermit={aquifer.pct_permit}
                  columnMode={columnMode}
                />
              </GroupRow>
              {expandedL1[aquifer.aquifer_ndx] &&
                aquifer.years.map((yearData) => {
                  const yearKey = `${aquifer.aquifer_ndx}-${yearData.year}`;
                  return (
                    <React.Fragment key={yearKey}>
                      <ChildRow
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleExpandL2(yearKey);
                        }}
                      >
                        <IndentedCell $indent={3}>
                          <ExpandIcon>
                            {expandedL2[yearKey] ? (
                              <ExpandMoreIcon fontSize="small" />
                            ) : (
                              <ChevronRightIcon fontSize="small" />
                            )}
                          </ExpandIcon>
                          {yearData.year}
                        </IndentedCell>
                        <DataCells
                          totals={yearData.totals}
                          pctPermit={yearData.pct_permit}
                          columnMode={columnMode}
                        />
                      </ChildRow>
                      {expandedL2[yearKey] &&
                        yearData.aggSystems.map((agg) => (
                          <GrandchildRow
                            key={`${yearKey}-${agg.agg_system_ndx}`}
                          >
                            <IndentedCell $indent={5}>
                              {agg.agg_system_name}
                            </IndentedCell>
                            <DataCells
                              totals={agg.totals}
                              pctPermit={agg.pct_permit}
                              columnMode={columnMode}
                            />
                          </GrandchildRow>
                        ))}
                    </React.Fragment>
                  );
                })}
            </React.Fragment>
          ))}
          <TotalsRow>
            <StickyFirstCell>
              <strong>Totals</strong>
            </StickyFirstCell>
            <DataCells
              totals={summary.grandTotals}
              pctPermit={summary.grandTotals.pct_permit}
              columnMode={columnMode}
            />
          </TotalsRow>
        </TableBody>
      </Table>
    </SummaryTableContainer>
  );
};

export default AquiferSummaryTable;
